import React, { useEffect, useState } from 'react'

import { Alert, Box, Card, CardContent, Grid, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { getDeliveryCountries } from '../../../../redux/states/offers/getDeliveryCountries/getDeliveryCountries.slice'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import { administrativeDivisionLabels, deliveryDetailsInitialValues } from './DeliveryDetails.dictionary'
import styles from './DeliveryDetails.styles'
import { DeliveryDetailsProps, DeliveryDetailsValues } from './DeliveryDetails.types'

const DeliveryDetails: React.FC<DeliveryDetailsProps> = (props) => {
  const { onFormChange, sourceCountry, deliveryToTravelerWindow } = props
  const [selectedCountry, setSelectedCountry] = useState<string>('')

  const dispatch = useAppDispatch()

  // Helper function to get the appropriate label for administrative division
  const getAdministrativeDivisionLabel = (countryCode: string): string => {
    return administrativeDivisionLabels[countryCode] || 'State/Province/Region'
  }

  const [administrativeDivisionLabel, setAdministrativeDivisionLabel] = useState('State/Province/Region')

  // Fetch delivery countries on component mount
  useEffect(() => {
    dispatch(getDeliveryCountries({}))
  }, [dispatch])

  const initialValues: DeliveryDetailsValues = deliveryDetailsInitialValues

  const validationSchema = Yup.object({
    streetAddress: Yup.string()
      .required('Street address is required')
      .min(5, 'Street address must be at least 5 characters')
      .max(100, 'Street address must be less than 100 characters'),
    country: sourceCountry ? Yup.string() : Yup.string().required('Country is required'),
    administrativeDivision: Yup.string().required(`${administrativeDivisionLabel} is required`),
    city: Yup.string().required('City is required'),
    postalCode: Yup.string()
      .required('Postal/Zip code is required')
      .matches(/^[A-Za-z0-9\s-]+$/, 'Invalid postal/zip code format')
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    // eslint-disable-next-line no-empty-function
    onSubmit: () => {},
    validateOnChange: true,
    validateOnBlur: true
  })

  // Set source country when provided
  useEffect(() => {
    if (sourceCountry) {
      setSelectedCountry(sourceCountry.iataCode)
      formik.setFieldValue('country', sourceCountry.iataCode)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Update administrative division label when country changes
    if (selectedCountry) {
      setAdministrativeDivisionLabel(getAdministrativeDivisionLabel(selectedCountry))
    } else {
      setAdministrativeDivisionLabel('State/Province/Region')
    }
  }, [selectedCountry])

  const getStayDateText = () => {
    if (deliveryToTravelerWindow) {
      const startDate = date(deliveryToTravelerWindow.startDate).format(dateTimeFormats.date.medium)
      const endDate = date(deliveryToTravelerWindow.endDate).format(dateTimeFormats.date.medium)

      return `Items will be delivered between ${startDate} and ${endDate}`
    }

    return 'Delivery window is based on departure to delivery country'
  }

  // Update parent component when form values or validity changes
  useEffect(() => {
    const values = formik.values

    // Check if all required fields are filled
    const hasAllRequiredFields = Boolean(
      values.streetAddress && values.city && values.administrativeDivision && values.country && values.postalCode
    )

    const hasNoErrors = Object.keys(formik.errors).length === 0

    // Form is valid if all fields are filled and there are no errors
    const isValid = hasAllRequiredFields && hasNoErrors

    onFormChange(values, isValid)
  }, [formik.values, formik.errors, onFormChange])

  return (
    <Card sx={styles.container}>
      <CardContent>
        <Alert severity="info" sx={styles.warningBox}>
          <Typography sx={styles.warningText}>
            {deliveryToTravelerWindow
              ? `Please provide the delivery address. Items will be delivered to this address between ${date(deliveryToTravelerWindow.startDate).format(dateTimeFormats.date.medium)} and ${date(deliveryToTravelerWindow.endDate).format(dateTimeFormats.date.medium)} to ensure they arrive before your departure.`
              : 'Please provide the delivery address. Items will be delivered to this address within the calculated delivery window based on your flight departure date to ensure timely arrival.'}
          </Typography>
        </Alert>

        <Typography variant="h6" sx={styles.sectionTitle}>
          Delivery Address
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="streetAddress"
              label="Street Address"
              placeholder="Enter the street address where items will be delivered"
              value={formik.values.streetAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
              helperText={formik.touched.streetAddress && formik.errors.streetAddress}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth name="country" label="Country" value={sourceCountry?.name ?? ''} disabled={true} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="administrativeDivision"
              label={administrativeDivisionLabel}
              placeholder={`Enter ${administrativeDivisionLabel.toLowerCase()}`}
              value={formik.values.administrativeDivision}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.administrativeDivision && Boolean(formik.errors.administrativeDivision)}
              helperText={formik.touched.administrativeDivision && formik.errors.administrativeDivision}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="city"
              label="City"
              placeholder="Enter city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="postalCode"
              label="Postal/Zip Code"
              placeholder="Enter postal or zip code"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
              helperText={formik.touched.postalCode && formik.errors.postalCode}
              required
            />
          </Grid>
        </Grid>
      </CardContent>

      <CardContent>
        <Typography variant="h6" sx={styles.sectionTitle}>
          Delivery Window
        </Typography>

        <Box sx={styles.datePickerBox(false)}>
          <Typography sx={styles.datePickerText(false)}>{getStayDateText()}</Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default DeliveryDetails
